// imageCompress.ts

// presigned 직접 업로드로 서버 페이로드 한계(ALB 10MB / PHP upload_max_filesize)가
// 사라져 압축을 완화했다. 이제 압축은 서버가 문서화한 S3 소프트캡(10 MiB)만 넘지
// 않게 하는 안전장치. 필요하면 화면별로 더 낮춰도 된다.
export const IMAGE_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

// presign 허용 MIME (서버와 동일). 비허용(gif/heic 등)은 canvas 재인코딩으로
// jpeg 로 정규화되도록 아래 early-return 에서 통과시키지 않는다.
const PRESIGN_ALLOWED_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
]);

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("CANVAS_TO_BLOB_FAILED"));
        resolve(blob);
      },
      mimeType,
      quality
    );
  });
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function pickOutputMime(input: File) {
  if (input.type === "image/png") return "image/jpeg";
  if (input.type === "image/webp") return "image/webp";
  return "image/jpeg";
}

export async function compressImage(file: File, maxBytes: number = IMAGE_UPLOAD_MAX_BYTES): Promise<File> {
  // 소프트캡 이하이면서 허용 MIME 이면 원본 그대로. 비허용 MIME 은 크기와
  // 무관하게 아래 canvas 경로로 내려가 jpeg/webp 로 정규화된다(presign 호환).
  if (file.size <= maxBytes && PRESIGN_ALLOWED_MIME.has(file.type)) return file;

  const img = await loadImageFromFile(file);

  let maxSide = 1600;
  let quality = 0.82;
  const outMime = pickOutputMime(file);

  for (let attempt = 0; attempt < 12; attempt++) {
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const targetW = Math.max(1, Math.round(img.width * scale));
    const targetH = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("NO_CANVAS_CONTEXT");

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    if (outMime === "image/jpeg") {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, targetW, targetH);
    }

    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await canvasToBlob(canvas, outMime, quality);

    if (blob.size <= maxBytes) {
      const ext = outMime === "image/webp" ? "webp" : "jpg";
      const name = (file.name?.replace(/\.[^/.]+$/, "") || "image") + `.${ext}`;
      return new File([blob], name, { type: outMime });
    }

    if (quality > 0.55) {
      quality = clamp(quality - 0.08, 0.5, 0.95);
    } else {
      maxSide = Math.round(maxSide * 0.85);
      quality = 0.8;
    }
  }

  const fallbackMaxSide = 1000;
  const scale = Math.min(1, fallbackMaxSide / Math.max(img.width, img.height));
  const targetW = Math.max(1, Math.round(img.width * scale));
  const targetH = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("NO_CANVAS_CONTEXT");

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(img, 0, 0, targetW, targetH);

  const blob = await canvasToBlob(canvas, "image/jpeg", 0.6);
  const name = (file.name?.replace(/\.[^/.]+$/, "") || "image") + ".jpg";
  return new File([blob], name, { type: "image/jpeg" });
}
